/**
 * 顧客詳細「ドイツ報告」タブ (2026-08-24)。
 *
 * 取扱商品がドイツ製のため、購入者の 電話番号 / メールアドレス / 英語氏名 / 英語住所 の
 * 4 項目をメーカー (ドイツ) へ報告する必要がある。このタブは:
 *   ① 「いつ・誰が・どの内容で報告したか」を記録する (追記専用台帳 germany_reports)
 *   ② 記録は報告時点のスナップショットで凍結され、顧客データが後から変わっても残る
 *      (firestore.rules で update / delete 禁止)
 *   ③ 報告内容の変更は、担当者が「この内容で報告済みとして記録」を明示操作した時だけ
 *      新しい記録として積み重なる (自動反映では記録されない)
 *   ④ 現在の顧客データと直近報告の一致 / 差異を項目ごとに表示する
 *
 * データモデル: shared/src/types/mail.ts の GermanyReport。
 * 差異判定: shared/src/utils/germany-report.ts の diffGermanyReport (純関数)。
 */
import type { GermanyReport, MailContact } from '@goenhub/shared';
import {
  GERMANY_REPORT_FIELD_KEYS,
  buildGermanyReportSnapshot,
  diffGermanyReport,
  formatGermanyReportValue,
  isGermanyReportUpToDate,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import {
  AlertTriangle,
  CheckCircle2,
  Flag,
  Send,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import { db } from '@/lib/firebase';
import { safeAddDoc } from '@/lib/safeWrite';

interface Props {
  contactId: string;
  contact: MailContact;
}

interface GermanyReportRecord extends GermanyReport {
  id: string;
}

function fmtDateTime(v: unknown): string {
  if (!v) return '-';
  const d =
    typeof v === 'string'
      ? new Date(v)
      : typeof v === 'object' && v !== null && 'toDate' in v
        ? (v as { toDate: () => Date }).toDate()
        : null;
  if (!d || Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** germany_reports 一覧 (最新順)。追記専用のためリアルタイム購読は不要。 */
function useGermanyReports(contactId: string) {
  return useQuery({
    queryKey: ['germany-reports', contactId],
    queryFn: async (): Promise<GermanyReportRecord[]> => {
      const snap = await getDocs(
        query(
          collection(db, 'mail_contacts', contactId, 'germany_reports'),
          orderBy('reportedAt', 'desc'),
          limit(200),
        ),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as GermanyReport),
      }));
    },
  });
}

export function ContactGermanyReportTab({ contactId, contact }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const reports = useGermanyReports(contactId);
  const [note, setNote] = useState('');

  const canRecord = user?.role === 'owner' || user?.role === 'editor';

  const latest = reports.data?.[0] ?? null;
  const diffs = useMemo(
    () => diffGermanyReport(contact, latest?.snapshot ?? null),
    [contact, latest],
  );
  const upToDate = latest != null && isGermanyReportUpToDate(diffs);
  const currentSnapshot = useMemo(
    () => buildGermanyReportSnapshot(contact),
    [contact],
  );
  const hasAnyCurrentValue = GERMANY_REPORT_FIELD_KEYS.some(
    (k) => formatGermanyReportValue(k, currentSnapshot) !== '',
  );
  const missingCurrent = diffs.filter((d) => !d.current);

  const record = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('ログイン情報を取得できませんでした');
      const doc: Omit<GermanyReport, 'reportedAt'> & {
        reportedAt: ReturnType<typeof serverTimestamp>;
      } = {
        snapshot: currentSnapshot,
        reportedByUid: user.uid,
        reportedByName: user.displayName || user.email || null,
        note: note.trim() || null,
        reportedAt: serverTimestamp(),
      };
      await safeAddDoc(
        collection(db, 'mail_contacts', contactId, 'germany_reports'),
        doc,
      );
    },
    onSuccess: () => {
      setNote('');
      void qc.invalidateQueries({ queryKey: ['germany-reports', contactId] });
      toast.success('ドイツへの報告記録を追加しました');
    },
    onError: (err) => {
      toast.error(`記録に失敗しました: ${(err as Error).message}`);
    },
  });

  const handleRecord = () => {
    const lines = diffs
      .map((d) => `・${d.label}: ${d.current || '（未登録）'}`)
      .join('\n');
    const warn =
      missingCurrent.length > 0
        ? `\n\n⚠️ 未登録の項目があります: ${missingCurrent.map((d) => d.label).join('・')}`
        : '';
    if (
      !window.confirm(
        `現在の顧客データの以下の内容で「メーカー（ドイツ）へ報告済み」として記録します。\n\n${lines}${warn}\n\n記録は履歴として残り、あとから編集・削除できません。よろしいですか？`,
      )
    )
      return;
    record.mutate();
  };

  return (
    <div className="space-y-5">
      {/* ── 状態サマリ + 現在データ vs 直近報告 ── */}
      <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Flag className="size-4 text-[var(--accent)]" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">
            メーカー（ドイツ）への報告状況
          </h3>
          {reports.isLoading ? null : !latest ? (
            <Badge variant="draft">未報告</Badge>
          ) : upToDate ? (
            <Badge variant="approved">
              <CheckCircle2 className="size-3" />
              報告済み・最新
            </Badge>
          ) : (
            <Badge variant="danger">
              <AlertTriangle className="size-3" />
              差異あり・再報告が必要
            </Badge>
          )}
        </div>
        <p className="text-xs text-[var(--text-muted)]">
          報告対象は 電話番号・メールアドレス・氏名（英語表記）・住所（英語表記）の 4
          項目です。フォーム経由の自動反映などで顧客データが変わっても、過去の報告記録はそのまま残ります。実際にドイツへ報告したら「報告済みとして記録」してください。
        </p>

        {reports.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : (
          <div className="overflow-x-auto rounded border border-[var(--border)]">
            <table className="w-full text-[12px] min-w-[640px]">
              <thead className="bg-[var(--bg-elevated)] text-[var(--text-secondary)]">
                <tr>
                  <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                    項目
                  </th>
                  <th className="text-left font-semibold px-3 py-2">
                    現在の顧客データ
                  </th>
                  <th className="text-left font-semibold px-3 py-2">
                    直近の報告内容
                    {latest ? ` (${fmtDateTime(latest.reportedAt)})` : ''}
                  </th>
                  <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                    状態
                  </th>
                </tr>
              </thead>
              <tbody>
                {diffs.map((d) => (
                  <tr
                    key={d.key}
                    className="border-t border-[var(--border)] align-top"
                  >
                    <td className="px-3 py-2 whitespace-nowrap font-semibold">
                      {d.label}
                    </td>
                    <td className="px-3 py-2 break-all">
                      {d.current || (
                        <span className="text-[var(--text-muted)] italic">
                          未登録
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 break-all">
                      {latest ? (
                        d.reported || (
                          <span className="text-[var(--text-muted)] italic">
                            （報告時 空欄）
                          </span>
                        )
                      ) : (
                        <span className="text-[var(--text-muted)] italic">
                          未報告
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {!latest ? (
                        <Badge variant="draft">未報告</Badge>
                      ) : d.matches ? (
                        <Badge variant="approved">一致</Badge>
                      ) : (
                        <Badge variant="danger">差異</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── 報告済みとして記録 ── */}
        {canRecord ? (
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2">
            <div className="text-xs font-semibold text-[var(--text-primary)]">
              ドイツへ報告済みとして記録する
            </div>
            <p className="text-[11px] text-[var(--text-muted)]">
              上の表の「現在の顧客データ」の内容で報告した、という記録を残します。内容を変更して再報告した場合も、この操作で新しい記録が履歴に追加されます（過去の記録は消えません）。
            </p>
            {missingCurrent.length > 0 && (
              <p className="text-[11px] text-[var(--accent-amber)]">
                ⚠️ 未登録の項目があります（
                {missingCurrent.map((d) => d.label).join('・')}
                ）。基本情報タブで登録するか、顧客情報確認・更新フォームを送って回収してから記録することを推奨します。
              </p>
            )}
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="メモ (任意。例: お客様依頼で住所変更のため再報告)"
              className="w-full px-3 py-2 text-xs rounded-md border border-[var(--border)] bg-[var(--bg-input)] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
              maxLength={500}
            />
            <div className="flex items-center justify-end gap-2">
              {latest && upToDate && (
                <span className="text-[11px] text-[var(--text-muted)]">
                  直近の報告と同じ内容です（再送した場合のみ記録してください）
                </span>
              )}
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleRecord}
                disabled={record.isPending || !hasAnyCurrentValue}
                title={
                  hasAnyCurrentValue
                    ? undefined
                    : '報告対象 4 項目がすべて未登録のため記録できません'
                }
              >
                {record.isPending ? (
                  <Spinner className="size-3 mr-1" />
                ) : (
                  <Send className="size-3.5 mr-1" />
                )}
                この内容で報告済みとして記録
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-[11px] text-[var(--text-muted)] italic">
            報告記録の追加は editor 以上のメンバーのみ可能です。
          </p>
        )}
      </div>

      {/* ── 報告履歴 ── */}
      <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">
          報告履歴
        </h3>
        {reports.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : !reports.data || reports.data.length === 0 ? (
          <EmptyState
            icon={Flag}
            title="報告記録はまだありません"
            description="「この内容で報告済みとして記録」を行うと、ここに履歴が追加されます。"
          />
        ) : (
          <div className="overflow-x-auto rounded border border-[var(--border)]">
            <table className="w-full text-[12px] min-w-[860px]">
              <thead className="bg-[var(--bg-elevated)] text-[var(--text-secondary)]">
                <tr>
                  <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                    報告日時
                  </th>
                  <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                    記録者
                  </th>
                  <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                    電話番号
                  </th>
                  <th className="text-left font-semibold px-3 py-2">
                    メールアドレス
                  </th>
                  <th className="text-left font-semibold px-3 py-2">
                    氏名（英語表記）
                  </th>
                  <th className="text-left font-semibold px-3 py-2">
                    住所（英語表記）
                  </th>
                  <th className="text-left font-semibold px-3 py-2">メモ</th>
                </tr>
              </thead>
              <tbody>
                {reports.data.map((r, i) => (
                  <tr
                    key={r.id}
                    className="border-t border-[var(--border)] align-top"
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-[var(--text-secondary)]">
                      {fmtDateTime(r.reportedAt)}
                      {i === 0 && (
                        <Badge variant="accent" className="ml-1.5">
                          直近
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.reportedByName || r.reportedByUid}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatGermanyReportValue('phone', r.snapshot) || '—'}
                    </td>
                    <td className="px-3 py-2 break-all">
                      {formatGermanyReportValue('email', r.snapshot) || '—'}
                    </td>
                    <td className="px-3 py-2 break-all">
                      {formatGermanyReportValue('nameEn', r.snapshot) || '—'}
                    </td>
                    <td className="px-3 py-2 break-all">
                      {formatGermanyReportValue('englishAddress', r.snapshot) ||
                        '—'}
                    </td>
                    <td className="px-3 py-2 break-all text-[var(--text-secondary)]">
                      {r.note || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-[var(--text-muted)]">
          記録は追記専用です（編集・削除不可）。誤って記録した場合は、正しい内容で再度記録し、メモに訂正の旨を残してください。
        </p>
      </div>
    </div>
  );
}
